import React from 'react';
import { Link } from 'react-router-dom';
import { ClompApi } from '../api/ClompApi';
import { endSession } from '../api/session';
import { timeAgo } from '../api/presence';
import { BingoSignups, BingoDraft } from './BingoDraft';
import './BingoPage.css';

// The Old School RuneScape bingo event. This page stands apart from the rest of the site: it has no navbar, and
// nothing links to it, so it is reached only by its own address. Anyone with the address can look; Admins and
// Founders (logged in with their clomp account, from the bar at the top) can edit the teams, tiles and timer and mark
// tiles as completed.

const api = new ClompApi();

// The most rows, and the most columns, the board can have
const SIDE_MAX = 10;
const TITLE_MAX = 80;
const POINTS_MAX = 1000;
const IMAGE_MAX = 500;
const TEAM_NAME_MAX = 40;
// The event's own name, and what it is called until one is given
const EVENT_TITLE_MAX = 60;
const DEFAULT_TITLE = 'Frog Bingo';
// A drop sent in for checking: its note
const NOTE_MAX = 200;

// How many pictures one submission can have (for a tile in more than one part)
const SUBMIT_IMAGES_MAX = 5;

// Whether a picture link looks right: https, no spaces
const isImageLink = (link) => /^https:\/\/\S+$/.test(link);
// The colours teams are drawn in until one is chosen (the backend's BINGO_TEAM_COLORS): a new team's picker starts on
// the next of these
const TEAM_COLORS = ['#4a90e2', '#d9443a', '#56a85a', '#e0b13a', '#9b6bd6', '#e07b39', '#3bb3ad', '#d65c9c'];
// Where an admin's "player view" is remembered for the rest of the visit, so a reload keeps it
const PLAYER_VIEW_KEY = 'bingoPlayerView';
// How often an open page checks for changes made by someone else; and while the draft is live, so that every pick
// shows up straight away
const REFRESH_SECONDS = 20;
const DRAFT_REFRESH_SECONDS = 3;
const NO_DRAFT = { status: 'off', order: [], picks: [], onClock: null };

// A tile can be split into tiers, each with its own task and points. Tier 1 is bronze, 2 silver, 3 gold.
const TIERS = [
    { id: 1, name: 'Bronze', color: '#cd7f32' },
    { id: 2, name: 'Silver', color: '#c4ccd4' },
    { id: 3, name: 'Gold', color: '#f2c94c' }
];

// The tiers of tile number i a team has done (none for a tile without tiers)
const tiersDone = (team, i) => (team && team.tiers && team.tiers[i]) || [];

// The best tier of tile number i a team has done, as a number (0 for none): only this one counts for points
const bestTier = (team, i) => Math.max(0, ...tiersDone(team, i));

// What a team scores for tile number i: nothing if it hasn't done it; for a tiered tile, the points of the best tier
// it has done; otherwise the tile's points
function tileScore(team, tiles, i) {
    let tile = tiles[i];
    if (!tile || !team.completed.includes(i)) {
        return 0;
    }
    if (tile.tiers && tile.tiers.length > 0) {
        let best = tile.tiers[bestTier(team, i) - 1];
        return best ? best.points : 0;
    }
    return tile.points;
}

// How far a team has got with tile number i, as a tier, for the row and column bonuses: 0 if not done; gold (3) for
// a done tile without tiers, or with its top tier done; otherwise the best tier done
function tileLevel(team, tiles, i) {
    let tile = tiles[i];
    if (!tile || !team.completed.includes(i)) {
        return 0;
    }
    let tiers = tile.tiers || [];
    let best = bestTier(team, i);
    return tiers.length === 0 || best >= tiers.length ? TIERS.length : best;
}

// The tier a line of tiles (tile numbers) is complete to for a team: the lowest of its tiles, 0 if any isn't done.
// Blank tiles don't count, and a line of nothing but blank ones is never complete.
function lineLevel(team, tiles, line) {
    let used = line.filter((i) => tiles[i] && (tiles[i].title || tiles[i].image));
    return used.length === 0 ? 0 : Math.min(...used.map((i) => tileLevel(team, tiles, i)));
}

// The row and column bonuses a team has earned on a board ({ tiles, rows, columns, lineBonus }):
// { rows: [tier], columns: [tier], points } with each line's tier (0 for not complete) and the bonus points they add
function lineBonuses(team, board) {
    let { tiles, rows, columns } = board;
    let bonus = board.lineBonus || [0, 0, 0];
    let range = (n) => Array.from({ length: n }, (_, k) => k);
    let rowLevels = range(rows).map((r) => lineLevel(team, tiles, range(columns).map((c) => r * columns + c)));
    let columnLevels = range(columns).map((c) => lineLevel(team, tiles, range(rows).map((r) => r * columns + c)));
    let points = [...rowLevels, ...columnLevels].reduce((sum, level) => sum + (level > 0 ? bonus[level - 1] || 0 : 0), 0);
    return { rows: rowLevels, columns: columnLevels, points };
}

// A team's score: what it has scored on each tile it has done, and its row and column bonuses
function teamScore(team, board) {
    return team.completed.reduce((sum, i) => sum + tileScore(team, board.tiles, i), 0) + lineBonuses(team, board).points;
}

// What each player's approved drops add to a team's points, tiles only (no row or column bonuses): a drop counts
// only while it is the best the team has of its tile, so a bronze stops counting once a teammate gets the gold.
// Every player on the team is listed, and anyone off it whose drops still count. [{ id, username, points }], most first.
function playerPoints(team, tiles) {
    let players = new Map((team.members || []).map((member) => [member.id, { ...member, points: 0 }]));
    for (let credit of team.credits || []) {
        let tile = tiles[credit.tile];
        if (!tile || !team.completed.includes(credit.tile)) {
            continue;
        }
        let tiered = Boolean(tile.tiers && tile.tiers.length > 0);
        let points = 0;
        if (tiered && credit.tier > 0 && credit.tier === bestTier(team, credit.tile)) {
            points = (tile.tiers[credit.tier - 1] || { points: 0 }).points;
        } else if (!tiered && credit.tier === 0) {
            points = tile.points;
        }
        if (!players.has(credit.by.id)) {
            if (points === 0) {
                continue;
            }
            players.set(credit.by.id, { ...credit.by, points: 0, former: true });
        }
        players.get(credit.by.id).points += points;
    }
    return [...players.values()].sort((a, b) => b.points - a.points || a.username.localeCompare(b.username));
}

// The points shown on a tile's corner: its own, or for a tiered tile the range from bronze to gold
function pointsLabel(tile) {
    if (!tile.tiers || tile.tiers.length === 0) {
        return String(tile.points);
    }
    let all = tile.tiers.map((tier) => tier.points);
    let low = Math.min(...all);
    let high = Math.max(...all);
    return low === high ? String(high) : `${low}–${high}`;
}

// An empty set of tier boxes for the tile form: one for each tier
const blankTiers = () => TIERS.map(() => ({ task: '', points: '' }));

const twoDigits = (number) => String(number).padStart(2, '0');

// A date as text (as the backend gives it) in the form a date-and-time input holds, in the viewer's own time zone
function toInputTime(date) {
    if (!date) {
        return '';
    }
    let d = new Date(date);
    return `${d.getFullYear()}-${twoDigits(d.getMonth() + 1)}-${twoDigits(d.getDate())}T${twoDigits(d.getHours())}:${twoDigits(d.getMinutes())}`;
}

// The event's timer: a countdown to the start, then to the end, then a note that it is over above a clock at zero. Shows nothing until
// both times are set. It keeps its own clock, so the rest of the page isn't drawn again every second.
class BingoTimer extends React.Component {

    state = { now: Date.now() };

    componentDidMount() {
        this.timer = setInterval(() => this.setState({ now: Date.now() }), 1000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    render() {
        let { startsAt, endsAt } = this.props;
        if (!startsAt || !endsAt) {
            return null;
        }
        let start = new Date(startsAt).getTime();
        let end = new Date(endsAt).getTime();
        let now = this.state.now;
        // once the event is over the clock stays at zero
        let finished = now >= end;
        let seconds = finished ? 0 : Math.ceil(((now < start ? start : end) - now) / 1000);
        let parts = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
        return (
            <div className="bingo-timer" role="timer">
                {finished ?
                    <span className="bingo-timer-finished">The bingo is finished</span>
                    :
                    <span className="bingo-timer-label">{now < start ? 'Bingo starts in' : 'Time remaining'}</span>}
                <span className="bingo-timer-digits">
                    <span className="bingo-timer-clock">{parts.map(twoDigits).join(':')}</span>
                    <span className="bingo-timer-units" aria-hidden="true"><span>days</span><span>hrs</span><span>min</span><span>sec</span></span>
                </span>
            </div>
        );
    }

}

export class BingoPage extends React.Component {

    state = {
        // The event's name, at the top of the page; for an admin, whether it is being renamed, what the box says, and
        // what went wrong
        title: DEFAULT_TITLE,
        editingTitle: false,
        titleDraft: '',
        titleError: null,
        // The bonus for completing a whole row or column, by its lowest tier: [bronze, silver, gold]; and for an admin,
        // what its boxes say while it is being changed (null when not)
        lineBonus: [0, 0, 0],
        bonusDraft: null,
        bonusError: null,
        // The board is rows x columns tiles, held in order from the top left, row by row
        rows: 0,
        columns: 0,
        tiles: [],
        // When the event starts and ends, as the backend gives them: both null until they are set
        startsAt: null,
        endsAt: null,
        // Staff: the form for those times, and what its two boxes say
        editingTimer: false,
        timerStart: '',
        timerEnd: '',
        timerError: null,
        teams: [],
        // The draft that puts the signed-up players on teams (see BingoDraft), and what went wrong with the last change to it
        draft: NO_DRAFT,
        draftError: null,
        // Who has signed up ({ open, signups, mine }), null until loaded
        signups: null,
        // Which tab shows: 'board', 'signups' or 'draft'; null until one is chosen (see view)
        view: null,
        loading: true,
        error: false,
        // Admins and Founders can change the event from this page
        canEdit: false,
        // An admin looking at the page as players see it: every editing control is hidden (see editable)
        playerView: (() => {
            try {
                return sessionStorage.getItem(PLAYER_VIEW_KEY) === 'yes';
            } catch (e) {
                return false;
            }
        })(),
        // The clomp account logged in on this browser ({ username }), null if none; checked tells whether that is
        // known yet
        account: null,
        accountChecked: false,
        // The id of the team whose completed tiles the board is showing
        selected: null,
        editingTeams: false,
        // What each team's name box and colour say while the teams are being edited, by team id; and what each team's
        // box for adding a player says
        names: {},
        memberDrafts: {},
        colors: {},
        newTeam: '',
        // The colour picked for a new team, or null for the next of TEAM_COLORS
        newTeamColor: null,
        // The id of the team whose Remove button has been pressed and is waiting to be confirmed
        confirmingRemove: null,
        editingTiles: false,
        // The row or column whose remove button has been pressed and is waiting to be confirmed:
        // { kind: 'row' or 'column', index }, or null
        confirmingLine: null,
        // The number of the tile whose form is open, or null
        tileOpen: null,
        tileTitle: '',
        tilePoints: '1',
        tileImage: '',
        // Whether the tile in the form is split into tiers, and what each tier's boxes say ({ task, points } as text)
        tileTiered: false,
        tileTiers: blankTiers(),
        // The number of the tile whose window is open (its drop or tiers, to read, send in, or for an admin to mark), or null
        tierOpen: null,
        // Drops sent in for checking: every one waiting (for admins), and the logged-in player's own
        submissions: [],
        mySubmissions: [],
        // The form for sending a drop in: whether it is open, what it says (drop is "tile:tier"), how it went
        submitOpen: false,
        submitTeam: '',
        submitDrop: '',
        // the picture links typed in, one box each
        submitImages: [''],
        submitNote: '',
        submitError: null,
        submitDone: false,
        // An admin's decisions on submissions: the one being saved, the one whose Deny waits to be confirmed, and
        // what went wrong
        reviewing: null,
        confirmingDeny: null,
        reviewError: null,
        // Looking up a picture on the Old School RuneScape Wiki: what has been typed, and the pages found for it
        // (null before anything has been looked up)
        wikiText: '',
        wikiResults: null,
        wikiSearching: false,
        wikiError: false,
        saving: false,
        // Something that went wrong with the last change, shown by whichever part it was made in
        teamError: null,
        boardError: null,
        tileError: null,
        tierError: null
    };

    // Counts the requests sent, so an answer is only shown if nothing newer has been asked since
    requests = 0;
    // The same, for looking things up on the wiki
    wikiRequests = 0;

    componentDidMount() {
        this.mounted = true;
        this.oldTitle = document.title;
        document.title = DEFAULT_TITLE;
        // keep the page out of search results
        this.robots = document.createElement('meta');
        this.robots.name = 'robots';
        this.robots.content = 'noindex';
        document.head.appendChild(this.robots);

        this.load();
        // every few seconds while the draft is live, else every REFRESH_SECONDS
        let ticks = 0;
        this.timer = setInterval(() => {
            ticks++;
            let due = this.state.draft.status === 'live' || ticks % Math.round(REFRESH_SECONDS / DRAFT_REFRESH_SECONDS) === 0;
            if (due && !document.hidden && !this.state.saving) {
                this.load();
                this.loadSubmissions();
                this.loadSignups();
            }
        }, DRAFT_REFRESH_SECONDS * 1000);

        this.checkAccount();
    }

    // Who is logged in here, and whether they may edit the event
    checkAccount() {
        if (localStorage.getItem('token') === null) {
            this.setState({ account: null, canEdit: false, accountChecked: true });
            this.loadSignups();
            return;
        }
        Promise.all([api.auth.getSession(), api.admin.getAccess()]).then(([session, access]) => {
            if (this.mounted) {
                this.setState({
                    account: session.loggedIn ? { username: session.username } : null,
                    canEdit: Boolean(session.loggedIn && access.admin),
                    accountChecked: true
                }, () => {
                    this.loadSubmissions();
                    this.loadSignups();
                    // a player on a team starts on their own team's board
                    let mine = this.myTeam();
                    if (mine && !this.picked) {
                        this.setState({ selected: mine.id });
                    }
                });
            }
        }).catch(() => {
            if (this.mounted) {
                this.setState({ accountChecked: true });
                this.loadSignups();
            }
        });
    }

    // Who has signed up for the event (what is sent depends on who is logged in)
    loadSignups = () => {
        api.bingo.getSignups().then((data) => {
            if (!this.mounted) {
                return;
            }
            // the tab to start on is chosen once, when the sign-ups first load (so signing up doesn't move the page on):
            // sign-ups for a player who hasn't signed up while they are open, otherwise the board
            if (this.firstView === undefined) {
                this.firstView = data.open && !data.mine && !this.editable() ? 'signups' : 'board';
            }
            this.setState({ signups: data });
        }).catch(() => {});
    }

    // The tab showing: the one picked, or else the draft while it is live, or else the one the page started on
    view() {
        let { view, draft } = this.state;
        if (view) {
            return view;
        }
        return draft.status === 'live' ? 'draft' : this.firstView || 'board';
    }

    // The team the logged-in player is on (their drops go in for it), or null
    myTeam(teams = this.state.teams) {
        let account = this.state.account;
        if (!account) {
            return null;
        }
        let name = account.username.toLowerCase();
        return teams.find((team) => (team.members || []).some((member) => member.username.toLowerCase() === name)) || null;
    }

    // Put a player on a team, by the username typed in its box
    addMember(team) {
        let username = (this.state.memberDrafts[team.id] || '').trim();
        if (!username) {
            return;
        }
        this.change(() => api.bingo.addMember(team.id, username), 'teamError').then((added) => {
            if (added && this.mounted) {
                this.setState({ memberDrafts: { ...this.state.memberDrafts, [team.id]: '' } });
            }
        });
    }

    // The drops waiting to be checked: every one for an admin, and the player's own
    loadSubmissions() {
        if (!this.state.account) {
            return;
        }
        api.bingo.getMySubmissions().then((data) => this.mounted && this.setState({ mySubmissions: data.submissions })).catch(() => {});
        if (this.state.canEdit) {
            api.bingo.getSubmissions().then((data) => this.mounted && this.setState({ submissions: data.submissions })).catch(() => {});
        }
    }

    // Whether the page shows its editing controls: for an Admin or Founder, unless they have switched to player view
    editable() {
        return this.state.canEdit && !this.state.playerView;
    }

    // Switch an admin between their own view and the one players get. Anything being edited is closed first.
    setPlayerView(playerView) {
        try {
            if (playerView) {
                sessionStorage.setItem(PLAYER_VIEW_KEY, 'yes');
            } else {
                sessionStorage.removeItem(PLAYER_VIEW_KEY);
            }
        } catch (e) { /* storage unavailable: it lasts until the page is reloaded */ }
        this.setState({ playerView, editingTitle: false, editingTeams: false, editingTiles: false, editingTimer: false, tileOpen: null, tierOpen: null, confirmingRemove: null, confirmingLine: null });
    }

    logOut = () => {
        let token = localStorage.getItem('token');
        api.auth.logout(token).catch(() => {});
        endSession(false);
        this.setState({ account: null, canEdit: false, submissions: [], mySubmissions: [], submitOpen: false, editingTeams: false, editingTiles: false, editingTimer: false, tileOpen: null });
        this.loadSignups();
    }

    componentWillUnmount() {
        this.mounted = false;
        clearInterval(this.timer);
        document.title = this.oldTitle;
        this.robots.remove();
    }

    // Show the event as the backend gave it, unless something newer has been asked for since
    show(request, board) {
        if (!this.mounted || request !== this.requests) {
            return;
        }
        let selected = this.state.selected;
        let mine = this.myTeam(board.teams);
        if (mine && !this.picked) {
            selected = mine.id;
        } else if (!board.teams.some((team) => team.id === selected)) {
            // nothing picked yet, or the picked team has been removed: show the first team
            let scored = this.scored(board.teams, board);
            selected = scored.length > 0 ? scored[0].id : null;
        }
        let title = board.title || DEFAULT_TITLE;
        document.title = title;
        this.setState({ title, lineBonus: board.lineBonus || [0, 0, 0], rows: board.rows, columns: board.columns, startsAt: board.startsAt || null, endsAt: board.endsAt || null, tiles: board.tiles, teams: board.teams, draft: board.draft || NO_DRAFT, selected, loading: false, error: false });
    }

    load() {
        let request = ++this.requests;
        api.bingo.getBoard().then((board) => this.show(request, board)).catch((err) => {
            console.log('Error loading bingo: ', err);
            if (this.mounted && this.state.loading) {
                this.setState({ loading: false, error: true });
            }
        });
    }

    // Send a change and show what comes back. errorKey is the state the message goes in if it fails. Resolves to
    // whether it worked.
    change(send, errorKey) {
        let request = ++this.requests;
        this.setState({ saving: true, [errorKey]: null });
        return send().then((board) => {
            this.show(request, board);
            if (this.mounted) {
                this.setState({ saving: false });
            }
            return true;
        }).catch((err) => {
            if (!this.mounted) {
                return false;
            }
            let status = err.response && err.response.status;
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({
                saving: false,
                [errorKey]: message || "Couldn't save that. Please try again.",
                // the login ran out, or isn't an admin one any more
                canEdit: status === 401 || status === 403 ? false : this.state.canEdit
            });
            // whatever was shown ahead of the answer may be wrong now
            this.load();
            return false;
        });
    }

    // Teams with their scores, in the order they were added: [{ id, name, completed, score }]
    // (board: { tiles, rows, columns, lineBonus }; bonus is what lineBonuses gives for the team)
    scored(teams = this.state.teams, board = this.state) {
        return teams.map((team) => ({ ...team, score: teamScore(team, board), bonus: lineBonuses(team, board) })).sort((a, b) => a.id - b.id);
    }

    // The colour a new team's picker shows: the one picked, or the next of the defaults
    newTeamColor() {
        return this.state.newTeamColor || TEAM_COLORS[this.state.teams.length % TEAM_COLORS.length];
    }

    addTeam = (e) => {
        e.preventDefault();
        let name = this.state.newTeam.trim();
        if (!name) {
            return;
        }
        this.change(() => api.bingo.addTeam(name, this.newTeamColor()), 'teamError').then((added) => {
            if (added && this.mounted) {
                this.setState({ newTeam: '', newTeamColor: null });
            }
        });
    }

    // What has been changed of a team in the editor: { name?, color? }, empty if nothing
    teamChanges(team) {
        let changes = {};
        let name = this.state.names[team.id];
        if (name !== undefined && name.trim() && name.trim() !== team.name) {
            changes.name = name.trim();
        }
        let color = this.state.colors[team.id];
        if (color !== undefined && color !== team.color) {
            changes.color = color;
        }
        return changes;
    }

    saveTeam(team) {
        let changes = this.teamChanges(team);
        if (Object.keys(changes).length === 0) {
            return;
        }
        this.change(() => api.bingo.updateTeam(team.id, changes), 'teamError').then((saved) => {
            if (saved && this.mounted) {
                let names = { ...this.state.names };
                let colors = { ...this.state.colors };
                delete names[team.id];
                delete colors[team.id];
                this.setState({ names, colors });
            }
        });
    }

    removeTeam(team) {
        this.change(() => api.bingo.deleteTeam(team.id), 'teamError').then(() => {
            if (this.mounted) {
                this.setState({ confirmingRemove: null });
            }
        });
    }

    // A tile was clicked: open its form while the tiles are being edited, otherwise mark it for the picked team
    clickTile(tile) {
        if (this.state.editingTiles) {
            let { title, points, image, tiers } = this.state.tiles[tile];
            tiers = tiers || [];
            this.wikiRequests++;
            this.setState({
                tileOpen: tile, tileTitle: title, tilePoints: String(points), tileImage: image, tileError: null,
                tileTiered: tiers.length > 0,
                tileTiers: blankTiers().map((blank, k) => tiers[k] ? { task: tiers[k].task, points: String(tiers[k].points) } : blank),
                wikiText: '', wikiResults: null, wikiSearching: false, wikiError: false
            });
            return;
        }
        // an admin with a team picked marks an ordinary tile with a click; any other tile, or anyone else, opens the
        // tile's window: its drop or tiers, to read, send in, or (for an admin) to mark
        let team = this.state.teams.find((other) => other.id === this.state.selected);
        if ((this.state.tiles[tile].tiers || []).length > 0 || !this.editable() || !team) {
            this.setState({ tierOpen: tile, boardError: null });
            return;
        }
        let completed = !team.completed.includes(tile);
        // shown straight away, ahead of the backend's answer
        this.setState({
            teams: this.state.teams.map((other) => other.id !== team.id ? other : {
                ...other,
                completed: completed ? [...other.completed, tile] : other.completed.filter((done) => done !== tile)
            })
        });
        this.change(() => api.bingo.setCompleted(team.id, tile, completed), 'boardError');
    }

    // Mark one tier of a tiered tile as done for the picked team, or not
    toggleTier(tile, tier) {
        let team = this.state.teams.find((other) => other.id === this.state.selected);
        if (!team) {
            return;
        }
        let completed = !tiersDone(team, tile).includes(tier);
        // shown straight away, ahead of the backend's answer
        this.setState({
            teams: this.state.teams.map((other) => {
                if (other.id !== team.id) {
                    return other;
                }
                let done = tiersDone(other, tile);
                done = completed ? [...done, tier] : done.filter((t) => t !== tier);
                let tiers = { ...other.tiers, [tile]: done };
                let rest = other.completed.filter((t) => t !== tile);
                return { ...other, tiers, completed: done.length > 0 ? [...rest, tile] : rest };
            })
        });
        this.change(() => api.bingo.setCompleted(team.id, tile, completed, tier), 'tierError');
    }

    // Whether a row or column has anything to lose: a tile that says or shows something, or one a team has completed
    lineInUse(kind, index) {
        let { tiles, teams, columns } = this.state;
        let inLine = (tile) => (kind === 'row' ? Math.floor(tile / columns) : tile % columns) === index;
        return tiles.some((tile, i) => inLine(i) && (tile.title || tile.image)) || teams.some((team) => team.completed.some(inLine));
    }

    // Take a row or column off the board, asking first if that would lose anything
    removeLine(kind, index, confirmed) {
        if (!confirmed && this.lineInUse(kind, index)) {
            this.setState({ confirmingLine: { kind, index }, boardError: null });
            return;
        }
        this.change(() => kind === 'row' ? api.bingo.removeRow(index) : api.bingo.removeColumn(index), 'boardError').then(() => {
            if (this.mounted) {
                this.setState({ confirmingLine: null });
            }
        });
    }

    saveBonus = (e) => {
        e.preventDefault();
        let points = this.state.bonusDraft.map((value) => Number(value));
        if (this.state.bonusDraft.some((value) => String(value).trim() === '') || !points.every((p) => Number.isInteger(p) && p >= 0 && p <= POINTS_MAX)) {
            this.setState({ bonusError: `Each bonus is a whole number from 0 to ${POINTS_MAX}` });
            return;
        }
        this.change(() => api.bingo.setLineBonus(points), 'bonusError').then((saved) => {
            if (saved && this.mounted) {
                this.setState({ bonusDraft: null });
            }
        });
    }

    // While the tiles are being edited: the bonus for a whole row or column, by the lowest tier it is complete to
    renderBonusSettings() {
        let { lineBonus, bonusDraft, bonusError, saving } = this.state;
        let draft = bonusDraft || lineBonus.map(String);
        let changed = bonusDraft !== null && bonusDraft.some((value, k) => String(value) !== String(lineBonus[k]));
        return (
            <form className="bingo-shape bingo-bonus-settings" onSubmit={this.saveBonus}>
                <span title="A whole row or column earns this, by the lowest tier its tiles are done to. Tiles without tiers count as Gold.">Row &amp; column bonus</span>
                {TIERS.map((tier, k) => (
                    <label key={tier.id} className="bingo-bonus-field" style={{ '--medal': tier.color }}>
                        <span className="bingo-medal is-done" aria-hidden="true" />
                        <span className="sr-only">{tier.name} bonus</span>
                        <input type="number" min="0" max={POINTS_MAX} step="1" value={draft[k]} title={`${tier.name}: a whole line done to at least ${tier.name}`}
                            onChange={(e) => this.setState({ bonusDraft: draft.map((value, j) => j === k ? e.target.value : value), bonusError: null })} />
                    </label>
                ))}
                <button type="submit" className="bingo-btn bingo-btn-ghost" disabled={saving || !changed}>Save bonus</button>
                {bonusError ? <p className="bingo-error" role="alert">{bonusError}</p> : null}
            </form>
        );
    }

    saveTitle = (e) => {
        e.preventDefault();
        let title = this.state.titleDraft.replace(/\s+/g, ' ').trim();
        if (!title) {
            this.setState({ titleError: 'The event needs a name' });
            return;
        }
        if (title === this.state.title) {
            this.setState({ editingTitle: false });
            return;
        }
        this.change(() => api.bingo.setTitle(title), 'titleError').then((saved) => {
            if (saved && this.mounted) {
                this.setState({ editingTitle: false });
            }
        });
    }

    // The event's name, with a pencil beside it for admins that turns it into a box to rename it
    renderTitle() {
        let { title, editingTitle, titleDraft, titleError, saving } = this.state;
        if (editingTitle && this.editable()) {
            let cancel = () => this.setState({ editingTitle: false, titleError: null });
            return (
                <form className="bingo-title bingo-title-form" onSubmit={this.saveTitle}>
                    <input type="text" aria-label="Name of the event" maxLength={EVENT_TITLE_MAX} autoFocus value={titleDraft}
                        onChange={(e) => this.setState({ titleDraft: e.target.value, titleError: null })}
                        onKeyDown={(e) => { if (e.key === 'Escape') { cancel(); } }} />
                    <button type="submit" className="bingo-btn bingo-btn-primary bingo-btn-small" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={cancel}>Cancel</button>
                    {titleError ? <p className="bingo-error" role="alert">{titleError}</p> : null}
                </form>
            );
        }
        return (
            <div className="bingo-title">
                <h1>{title}</h1>
                {this.editable() ?
                    <button type="button" className="bingo-title-edit" aria-label="Rename the event" title="Rename the event"
                        onClick={() => this.setState({ editingTitle: true, titleDraft: title, titleError: null })}>
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                            <path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                        </svg>
                    </button>
                    : null}
            </div>
        );
    }

    saveTimer = (e) => {
        e.preventDefault();
        let start = new Date(this.state.timerStart);
        let end = new Date(this.state.timerEnd);
        if (isNaN(start) || isNaN(end)) {
            this.setState({ timerError: 'Pick both a start and an end.' });
            return;
        }
        if (end <= start) {
            this.setState({ timerError: 'The end must be after the start.' });
            return;
        }
        this.changeTimer(start.toISOString(), end.toISOString());
    }

    // Set the event's times (or clear them with nulls) and close their form
    changeTimer(startsAt, endsAt) {
        this.change(() => api.bingo.setTimes(startsAt, endsAt), 'timerError').then((saved) => {
            if (saved && this.mounted) {
                this.setState({ editingTimer: false });
            }
        });
    }

    // The timer beside the page's name, and for staff the button and form that set it
    renderTimer() {
        let { startsAt, endsAt, editingTimer, timerStart, timerEnd, timerError, saving } = this.state;
        let canEdit = this.editable();
        let set = Boolean(startsAt && endsAt);
        if (!canEdit && !set) {
            return null;
        }
        return (
            <div className="bingo-timer-area">
                <BingoTimer startsAt={startsAt} endsAt={endsAt} />
                {canEdit && !editingTimer ?
                    <button type="button" className="bingo-btn bingo-btn-ghost"
                        onClick={() => this.setState({ editingTimer: true, timerStart: toInputTime(startsAt), timerEnd: toInputTime(endsAt), timerError: null })}>
                        {set ? 'Edit timer' : 'Set timer'}
                    </button>
                    : null}
                {canEdit && editingTimer ?
                    <form className="bingo-timer-form" onSubmit={this.saveTimer}>
                        <label htmlFor="bingo-timer-start">Starts</label>
                        <input id="bingo-timer-start" type="datetime-local" required value={timerStart} onChange={(e) => this.setState({ timerStart: e.target.value })} />
                        <label htmlFor="bingo-timer-end">Ends</label>
                        <input id="bingo-timer-end" type="datetime-local" required min={timerStart || undefined} value={timerEnd} onChange={(e) => this.setState({ timerEnd: e.target.value })} />
                        <p className="bingo-hint">Times are in your own time zone. Everyone sees the same countdown.</p>
                        <div className="bingo-timer-form-actions">
                            {set ? <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={() => this.changeTimer(null, null)}>Remove timer</button> : null}
                            <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={() => this.setState({ editingTimer: false })}>Cancel</button>
                            <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving}>Save</button>
                        </div>
                        {timerError ? <p className="bingo-error" role="alert">{timerError}</p> : null}
                    </form>
                    : null}
            </div>
        );
    }

    saveTile = (e) => {
        e.preventDefault();
        let { tileOpen, tileTitle, tilePoints, tileImage, tileTiered, tileTiers } = this.state;
        let points = Number(tilePoints);
        let tiers = [];
        if (tileTiered) {
            // the tiers filled in, bronze first: silver and gold can be left empty, but not one below a filled one
            let last = tileTiers.map((tier) => tier.task.trim() !== '').lastIndexOf(true);
            if (last < 0) {
                this.setState({ tileError: 'Write a task for the Bronze tier at least, or turn tiers off' });
                return;
            }
            for (let k = 0; k <= last; k++) {
                let task = tileTiers[k].task.trim();
                let tierPoints = Number(tileTiers[k].points);
                if (!task) {
                    this.setState({ tileError: `Write a task for the ${TIERS[k].name} tier, or leave the tiers above it empty` });
                    return;
                }
                if (String(tileTiers[k].points).trim() === '' || !Number.isInteger(tierPoints) || tierPoints < 0 || tierPoints > POINTS_MAX) {
                    this.setState({ tileError: `The ${TIERS[k].name} tier's points must be a whole number from 0 to ${POINTS_MAX}` });
                    return;
                }
                tiers.push({ task, points: tierPoints });
            }
            // the tile's own points don't count while it has tiers: keep what it had
            if (!Number.isInteger(points) || points < 0 || points > POINTS_MAX) {
                points = 1;
            }
        } else if (tilePoints.trim() === '' || !Number.isInteger(points) || points < 0 || points > POINTS_MAX) {
            this.setState({ tileError: `Points must be a whole number from 0 to ${POINTS_MAX}` });
            return;
        }
        this.change(() => api.bingo.setTile(tileOpen, tileTitle.trim(), points, tileImage.trim(), tiers), 'tileError').then((saved) => {
            if (saved && this.mounted) {
                this.setState({ tileOpen: null });
            }
        });
    }

    // Look up the pictures of wiki pages whose names start with what has been typed
    searchWiki = () => {
        let text = this.state.wikiText.trim();
        if (!text) {
            return;
        }
        let request = ++this.wikiRequests;
        this.setState({ wikiSearching: true, wikiError: false });
        api.bingo.searchWiki(text).then((results) => {
            if (this.mounted && request === this.wikiRequests) {
                this.setState({ wikiResults: results, wikiSearching: false });
            }
        }).catch(() => {
            if (this.mounted && request === this.wikiRequests) {
                this.setState({ wikiSearching: false, wikiError: true });
            }
        });
    }

    // Use a wiki page's picture for the open tile, and its name as the task if none has been written yet
    pickWiki(page) {
        this.setState({ tileImage: page.image, tileTitle: this.state.tileTitle.trim() ? this.state.tileTitle : page.title });
    }

    // The part of a tile's form for its picture: looking one up on the wiki, or pasting a link to any other
    renderImagePicker() {
        let { tileImage, wikiText, wikiResults, wikiSearching, wikiError } = this.state;
        return (
            <>
                <label htmlFor="bingo-wiki-text">Image</label>
                <div className="bingo-wiki-search">
                    <input id="bingo-wiki-text" type="text" placeholder="Search the OSRS Wiki, e.g. dragon pick" value={wikiText}
                        onChange={(e) => this.setState({ wikiText: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.searchWiki(); } }} />
                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={wikiSearching || !wikiText.trim()} onClick={this.searchWiki}>
                        {wikiSearching ? 'Searching...' : 'Search'}
                    </button>
                </div>
                {wikiError ? <p className="bingo-error" role="alert">Couldn't reach the wiki. Try again, or paste a link below.</p> : null}
                {wikiResults !== null && wikiResults.length === 0 ? <p className="bingo-hint">Nothing on the wiki starts with that. Try the start of the page's name.</p> : null}
                {wikiResults !== null && wikiResults.length > 0 ?
                    <ul className="bingo-wiki-results" aria-label="Wiki pages found">
                        {wikiResults.map((page) => (
                            <li key={page.title}>
                                <button type="button" className={page.image === tileImage ? 'bingo-wiki-picked' : ''} aria-pressed={page.image === tileImage}
                                    title={page.title} onClick={() => this.pickWiki(page)}>
                                    <img src={page.image} alt="" />
                                    <span>{page.title}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    : null}
                <div className="bingo-image-link">
                    {tileImage.trim() ? <img src={tileImage.trim()} alt="Chosen for this tile" /> : null}
                    <input type="text" aria-label="Image link" placeholder="or paste an image link (https://...)" maxLength={IMAGE_MAX} value={tileImage}
                        onChange={(e) => this.setState({ tileImage: e.target.value })} />
                    {tileImage ? <button type="button" className="bingo-btn bingo-btn-ghost" onClick={() => this.setState({ tileImage: '' })}>Remove</button> : null}
                </div>
            </>
        );
    }

    renderTeamEditor(scored) {
        let { names, colors, memberDrafts, newTeam, confirmingRemove, saving, teamError } = this.state;
        return (
            <div className="bingo-team-editor">
                {scored.length > 0 ?
                    <ul>
                        {scored.map((team) => {
                            let name = names[team.id] !== undefined ? names[team.id] : team.name;
                            return confirmingRemove === team.id ?
                                <li key={team.id} role="group" aria-label={`Remove ${team.name}`}>
                                    <span className="bingo-confirm">Remove {team.name} and its completed tiles?</span>
                                    <button type="button" className="bingo-btn bingo-btn-danger" disabled={saving} onClick={() => this.removeTeam(team)}>Yes, remove</button>
                                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} autoFocus onClick={() => this.setState({ confirmingRemove: null })}>Keep it</button>
                                </li>
                                :
                                <li key={team.id}>
                                    <input type="color" className="bingo-color" aria-label={`Colour of ${team.name}`} title="Team colour"
                                        value={colors[team.id] || team.color} onChange={(e) => this.setState({ colors: { ...colors, [team.id]: e.target.value } })} />
                                    <input type="text" aria-label={`Name of ${team.name}`} maxLength={TEAM_NAME_MAX} value={name}
                                        onChange={(e) => this.setState({ names: { ...names, [team.id]: e.target.value } })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { this.saveTeam(team); } }} />
                                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || !name.trim() || Object.keys(this.teamChanges(team)).length === 0}
                                        onClick={() => this.saveTeam(team)}>Save</button>
                                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={() => this.setState({ confirmingRemove: team.id, teamError: null })}>Remove</button>
                                    {/* the players on the team: their drops go in for it */}
                                    <div className="bingo-members">
                                        <span className="bingo-members-label">Players</span>
                                        {(team.members || []).length === 0 ? <span className="bingo-members-none">none yet</span> : null}
                                        {(team.members || []).map((member) => (
                                            <span key={member.id} className="bingo-member">
                                                {member.username}
                                                <button type="button" aria-label={`Take ${member.username} off ${team.name}`} title={`Take ${member.username} off ${team.name}`}
                                                    disabled={saving} onClick={() => this.change(() => api.bingo.removeMember(team.id, member.id), 'teamError')}>✕</button>
                                            </span>
                                        ))}
                                        <input type="text" className="bingo-member-input" aria-label={`Add a player to ${team.name}`} placeholder="clomp username"
                                            value={memberDrafts[team.id] || ''} onChange={(e) => this.setState({ memberDrafts: { ...memberDrafts, [team.id]: e.target.value }, teamError: null })}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { this.addMember(team); } }} />
                                        <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving || !(memberDrafts[team.id] || '').trim()}
                                            onClick={() => this.addMember(team)}>Add player</button>
                                    </div>
                                </li>;
                        })}
                    </ul>
                    : null}
                <form onSubmit={this.addTeam}>
                    <input type="color" className="bingo-color" aria-label="Colour of the new team" title="Team colour" value={this.newTeamColor()}
                        onChange={(e) => this.setState({ newTeamColor: e.target.value })} />
                    <input type="text" aria-label="New team name" placeholder="New team name" maxLength={TEAM_NAME_MAX} value={newTeam}
                        onChange={(e) => this.setState({ newTeam: e.target.value })} />
                    <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving || !newTeam.trim()}>Add team</button>
                </form>
                {teamError ? <p className="bingo-error" role="alert">{teamError}</p> : null}
            </div>
        );
    }

    // The players on the selected team, and the tile points their approved drops have added
    renderPlayers(team) {
        let players = playerPoints(team, this.state.tiles);
        return (
            <section className="bingo-players" aria-label={`${team.name} players`} style={{ '--team': team.color }}>
                <div className="bingo-panel-head">
                    <h2>Players <span>Tile points from their drops</span></h2>
                </div>
                {players.length === 0 ?
                    <p className="bingo-players-none">No players on {team.name} yet.</p>
                    :
                    <ol className="bingo-player-list">
                        {players.map((player) => (
                            <li key={player.id} className={'bingo-player' + (player.former ? ' bingo-player-former' : '')}>
                                <span className="bingo-team-swatch" aria-hidden="true" />
                                <Link className="bingo-player-name" to={`/profile/${encodeURIComponent(player.username)}`}>{player.username}</Link>
                                {player.former ? <small>left the team</small> : null}
                                <span className="bingo-player-points">{player.points}<span className="sr-only"> points</span></span>
                            </li>
                        ))}
                    </ol>}
            </section>
        );
    }

    renderScores(scored) {
        let { editingTeams, selected, tiles } = this.state;
        let canEdit = this.editable();
        let top = Math.max(0, ...scored.map((team) => team.score));
        let leaders = scored.filter((team) => team.score === top).length;
        let team = scored.find((other) => other.id === selected);
        return (
            <>
                <section className="bingo-scores" aria-label="Scoreboard">
                    <div className="bingo-panel-head">
                        <h2>Teams <span>Tap a team to see its board</span></h2>
                        {canEdit ?
                            <button type="button" className="bingo-btn bingo-btn-ghost" aria-pressed={editingTeams}
                                onClick={() => this.setState({ editingTeams: !editingTeams, confirmingRemove: null, names: {}, colors: {}, teamError: null })}>
                                {editingTeams ? 'Done' : 'Edit teams'}
                            </button>
                            : null}
                    </div>
                    {scored.length === 0 ?
                        <p className="bingo-status">{canEdit ? 'No teams yet. Use Edit teams to add some.' : 'No teams yet.'}</p>
                        :
                        <ol className="bingo-team-list">
                            {scored.map((other) => {
                                // its place by score (teams level on points share one), and whether it is ahead
                                let rank = scored.filter((rival) => rival.score > other.score).length + 1;
                                let leading = top > 0 && other.score === top;
                                return (
                                    <li key={other.id}>
                                        <button type="button" className={'bingo-team' + (other.id === selected ? ' bingo-team-selected' : '')}
                                            style={{ '--team': other.color }} aria-pressed={other.id === selected}
                                            onClick={() => {
                                                this.picked = true;
                                                this.setState({ selected: other.id, boardError: null });
                                            }}>
                                            <span className="bingo-team-rank">{rank}</span>
                                            <span className="bingo-team-swatch" aria-hidden="true" />
                                            <span className="bingo-team-name">{other.name}</span>
                                            <span className="bingo-team-bar" aria-hidden="true">
                                                <span style={{ width: `${top > 0 ? (other.score / top) * 100 : 0}%` }} />
                                            </span>
                                            <span className="bingo-team-score">{other.score}<span className="sr-only"> points</span></span>
                                            <span className="bingo-team-lead">{leading ? <span>{leaders > 1 ? 'Tied' : 'Leading'}</span> : null}</span>
                                            <span className="bingo-team-arrow" aria-hidden="true">›</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>}
                    {canEdit && editingTeams ? this.renderTeamEditor(scored) : null}
                </section>
                {team ? this.renderPlayers(team) : null}
                {team ?
                    <div className="bingo-stats" aria-label={`${team.name} so far`}>
                        <div className="bingo-stat">
                            <span>Total points</span>
                            <strong className="bingo-stat-gold">{team.score}</strong>
                            {team.bonus.points > 0 ? <small className="bingo-stat-note">incl. +{team.bonus.points} row and column bonus</small> : null}
                        </div>
                        <div className="bingo-stat">
                            <span>Tiles complete</span>
                            <strong>{team.completed.length} <small>/ {tiles.length}</small></strong>
                        </div>
                    </div>
                    : null}
            </>
        );
    }

    renderBoard(scored) {
        let { rows, columns, tiles, editingTiles, selected, boardError, confirmingLine, saving, lineBonus } = this.state;
        // the bonus markers at the end of each row and under each column, once a bonus is set
        let showBonus = !editingTiles && lineBonus.some((points) => points > 0);
        let canEdit = this.editable();
        let team = scored.find((other) => other.id === selected);
        let completed = team ? team.completed : [];
        // points are only worth showing on the tiles when they aren't all the same
        let showPoints = tiles.some((tile) => pointsLabel(tile) !== pointsLabel(tiles[0]));
        let anyTiers = tiles.some((tile) => (tile.tiers || []).length > 0);
        let hint = null;
        if (canEdit) {
            hint = editingTiles ? 'Click a tile to change what it says, or to split it into Bronze, Silver and Gold tiers. Use ✕ to remove its row or column.'
                : team ? `Click a tile to mark it completed for ${team.name}, or again to unmark it.${anyTiers ? ' Tiles with tiers open them instead.' : ''}`
                : 'Add a team to start marking tiles as completed.';
        } else if (anyTiers) {
            hint = 'Some tiles have Bronze, Silver and Gold tiers: click a tile to see them. Only the best tier a team completes counts.';
        }
        if (showBonus) {
            let bonusText = `A whole row or column earns a bonus by its lowest tier: ${TIERS.map((tier, k) => `${tier.name} +${lineBonus[k]}`).join(', ')}.`;
            hint = hint ? `${hint} ${bonusText}` : bonusText;
        }
        return (
            <section className="bingo-board-section" aria-label="Board" style={team ? { '--team': team.color } : undefined}>
                <div className="bingo-section-header">
                    <h2>{team ? <><span className="bingo-board-swatch" aria-hidden="true" />{team.name}'s board</> : 'Board'}</h2>
                    <div className="bingo-board-actions">
                        {!editingTiles ?
                            <button type="button" className="bingo-btn bingo-btn-primary" onClick={() => this.openSubmit(null)}>Submit a drop</button>
                            : null}
                        {canEdit ?
                            <button type="button" className="bingo-btn bingo-btn-ghost" aria-pressed={editingTiles}
                                onClick={() => this.setState({ editingTiles: !editingTiles, tileOpen: null, boardError: null, confirmingLine: null })}>
                                {editingTiles ? 'Done' : 'Edit tiles'}
                            </button>
                            : null}
                    </div>
                </div>
                {hint ? <p className="bingo-hint">{hint}</p> : null}
                {canEdit && editingTiles ?
                    <div className="bingo-shape">
                        <span>{rows} rows × {columns} columns</span>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || rows >= SIDE_MAX}
                            onClick={() => this.change(() => api.bingo.addRow(), 'boardError')}>Add row</button>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || columns >= SIDE_MAX}
                            onClick={() => this.change(() => api.bingo.addColumn(), 'boardError')}>Add column</button>
                    </div>
                    : null}
                {canEdit && editingTiles ? this.renderBonusSettings() : null}
                {canEdit && editingTiles && confirmingLine ?
                    <div className="bingo-shape" role="group" aria-label={`Remove ${confirmingLine.kind} ${confirmingLine.index + 1}`}>
                        <span className="bingo-confirm">Remove {confirmingLine.kind} {confirmingLine.index + 1}, with its tiles and who completed them?</span>
                        <button type="button" className="bingo-btn bingo-btn-danger" disabled={saving}
                            onClick={() => this.removeLine(confirmingLine.kind, confirmingLine.index, true)}>Yes, remove</button>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} autoFocus onClick={() => this.setState({ confirmingLine: null })}>Keep it</button>
                    </div>
                    : null}
                {boardError ? <p className="bingo-error" role="alert">{boardError}</p> : null}
                <div className="bingo-board-scroll">
                    {/* while the tiles are being edited, a remove button heads each column and each row */}
                    <div className={'bingo-board' + (editingTiles ? ' bingo-board-editing' : '')}
                        style={{
                            gridTemplateColumns: (editingTiles ? '26px ' : '') + `repeat(${columns}, minmax(0, 1fr))` + (showBonus ? ' 44px' : ''),
                            // readable tiles: on a phone the board scrolls sideways rather than squeezing them
                            minWidth: columns * 62,
                            // as wide as fits, but no wider than lets its rows fit in the window's height
                            maxWidth: `min(${columns * 150 + (showBonus ? 50 : 0)}px, calc((100vh - ${showBonus ? 230 : 190}px) * ${columns / rows} + ${showBonus ? 50 : 0}px))`
                        }}>
                        {editingTiles ?
                            <>
                                <span />
                                {tiles.slice(0, columns).map((tile, column) => (
                                    <button type="button" key={'column' + column} className="bingo-line-remove" disabled={saving || columns <= 1}
                                        aria-label={`Remove column ${column + 1}`} title={`Remove column ${column + 1}`}
                                        onClick={() => this.removeLine('column', column)}>✕</button>
                                ))}
                            </>
                            : null}
                        {tiles.map((tile, i) => {
                            let rowRemove = editingTiles && i % columns === 0 ?
                                <button type="button" key={'row' + i} className="bingo-line-remove" disabled={saving || rows <= 1}
                                    aria-label={`Remove row ${i / columns + 1}`} title={`Remove row ${i / columns + 1}`}
                                    onClick={() => this.removeLine('row', i / columns)}>✕</button>
                                : null;
                            let done = !editingTiles && completed.includes(i);
                            let blank = !tile.title && !tile.image;
                            let tiered = (tile.tiers || []).length > 0;
                            let doneTiers = editingTiles ? [] : tiersDone(team, i);
                            // a done tiered tile is drawn in the colour of the best tier done, not the team's
                            let tierStyle = tiered && done && doneTiers.length > 0 ? { '--team': TIERS[bestTier(team, i) - 1].color } : undefined;
                            let className = 'bingo-tile' + (done ? ' bingo-tile-done' : '') + (blank ? ' bingo-tile-blank' : '') + (tiered ? ' bingo-tile-tiered' : '');
                            let content = (
                                <>
                                    {showPoints && !blank ? <span className="bingo-tile-points">{pointsLabel(tile)}</span> : null}
                                    {tile.image ? <img className="bingo-tile-image" src={tile.image} alt="" /> : null}
                                    {tile.title || blank ? <span className="bingo-tile-title">{tile.title || (editingTiles ? '+' : '')}</span> : null}
                                    {done ? <span className="bingo-tile-check" aria-hidden="true">✓</span> : null}
                                </>
                            );
                            let tierText = tiered ? `, ${tile.tiers.length} tiers` + (team && doneTiers.length > 0 ? `, best done: ${TIERS[bestTier(team, i) - 1].name}` : '') : '';
                            // tiles are buttons only for someone who can do something by clicking them: an admin, or
                            // anyone for a tiered tile (to read its tiers)
                            let shown = (canEdit && editingTiles) || (!editingTiles && !blank) ?
                                <button type="button" key={i} className={className} style={tierStyle} aria-pressed={editingTiles || tiered ? undefined : done}
                                    aria-haspopup={!editingTiles && (tiered || !canEdit || !team) ? 'dialog' : undefined}
                                    aria-label={editingTiles ? `Edit tile ${i + 1}: ${tile.title || (blank ? 'blank' : 'image only')}` : `${tile.title || `Tile ${i + 1}`}${tierText}`}
                                    onClick={() => this.clickTile(i)}>{content}</button>
                                :
                                <div key={i} className={className} style={tierStyle}>{content}{done ? <span className="sr-only"> (completed)</span> : null}</div>;
                            // after the last tile of a row, that row's bonus
                            let rowBonus = showBonus && i % columns === columns - 1 ? this.renderBonusMark(team, 'row', Math.floor(i / columns)) : null;
                            return [rowRemove, shown, rowBonus].filter(Boolean);
                        })}
                        {/* under the board, each column's bonus */}
                        {showBonus ? Array.from({ length: columns }, (_, c) => this.renderBonusMark(team, 'column', c)) : null}
                    </div>
                </div>
            </section>
        );
    }

    // One row's or column's bonus marker: what the picked team has earned on it, by its lowest tier, or a dash
    renderBonusMark(team, kind, index) {
        let level = team ? team.bonus[kind + 's'][index] : 0;
        let name = `${kind === 'row' ? 'Row' : 'Column'} ${index + 1}`;
        if (level === 0) {
            return (
                <span key={kind + 'bonus' + index} className={`bingo-bonus bingo-bonus-${kind}`} title={`${name}: not complete yet`}>
                    <span aria-hidden="true">–</span><span className="sr-only">{name} bonus: not complete yet</span>
                </span>
            );
        }
        let tier = TIERS[level - 1];
        let points = this.state.lineBonus[level - 1];
        return (
            <span key={kind + 'bonus' + index} className={`bingo-bonus bingo-bonus-${kind} is-earned`} style={{ '--medal': tier.color }}
                title={`${name} complete to ${tier.name}: +${points} bonus`}>
                +{points}<span className="sr-only"> {name} bonus, complete to {tier.name}</span>
            </span>
        );
    }

    // The form for what a tile says, shows and is worth, over the page
    renderTileForm() {
        let { tileOpen, tileTitle, tilePoints, tileTiered, tileTiers, saving, tileError, columns } = this.state;
        let close = () => this.setState({ tileOpen: null });
        return (
            <div className="bingo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { close(); } }}
                onKeyDown={(e) => { if (e.key === 'Escape') { close(); } }}>
                <form className="bingo-tile-form" role="dialog" aria-modal="true" aria-labelledby="bingo-tile-form-title" onSubmit={this.saveTile}>
                    <h2 id="bingo-tile-form-title">Tile {tileOpen + 1} <span>row {Math.floor(tileOpen / columns) + 1}, column {tileOpen % columns + 1}</span></h2>
                    <label htmlFor="bingo-tile-title">Task</label>
                    <textarea id="bingo-tile-title" rows="3" maxLength={TITLE_MAX} autoFocus value={tileTitle} placeholder="e.g. Obtain a dragon pickaxe"
                        onChange={(e) => this.setState({ tileTitle: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') { this.saveTile(e); } }} />
                    {this.renderImagePicker()}
                    <label className="bingo-check">
                        <input type="checkbox" checked={tileTiered} onChange={(e) => this.setState({ tileTiered: e.target.checked })} />
                        Split into Bronze, Silver and Gold tiers
                    </label>
                    {tileTiered ?
                        <div className="bingo-tier-fields">
                            {TIERS.map((tier, k) => (
                                <div key={tier.id} className="bingo-tier-field" style={{ '--medal': tier.color }}>
                                    <span className="bingo-tier-name"><span className="bingo-medal is-done" aria-hidden="true" />{tier.name}</span>
                                    <input type="text" aria-label={`${tier.name} task`} maxLength={TITLE_MAX} value={tileTiers[k].task}
                                        placeholder={k === 0 ? 'e.g. 1x any raid purple' : 'Optional'}
                                        onChange={(e) => this.setState({ tileTiers: tileTiers.map((other, j) => j === k ? { ...other, task: e.target.value } : other) })} />
                                    <input type="number" aria-label={`${tier.name} points`} min="0" max={POINTS_MAX} step="1" placeholder="Pts" value={tileTiers[k].points}
                                        onChange={(e) => this.setState({ tileTiers: tileTiers.map((other, j) => j === k ? { ...other, points: e.target.value } : other) })} />
                                </div>
                            ))}
                            <p className="bingo-hint">A team scores only the best tier it completes. Leave Silver or Gold empty for fewer tiers. Changing the tiers clears completions that no longer fit.</p>
                        </div>
                        :
                        <>
                            <label htmlFor="bingo-tile-points">Points</label>
                            <input id="bingo-tile-points" type="number" min="0" max={POINTS_MAX} step="1" required value={tilePoints}
                                onChange={(e) => this.setState({ tilePoints: e.target.value })} />
                        </>}
                    <div className="bingo-tile-form-actions">
                        <span className="bingo-count">{tileTitle.length} / {TITLE_MAX}</span>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={close}>Cancel</button>
                        <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    </div>
                    {tileError ? <p className="bingo-error" role="alert">{tileError}</p> : null}
                </form>
            </div>
        );
    }

    // A tiered tile's tiers, over the page: each tier's task and points, which the picked team has done, and for an
    // admin a tick box for each to mark it
    renderTierDialog(scored) {
        let { tierOpen, tiles, selected, saving, tierError, mySubmissions, account } = this.state;
        let tile = tiles[tierOpen];
        if (!tile) {
            return null;
        }
        let tiered = (tile.tiers || []).length > 0;
        let team = scored.find((other) => other.id === selected);
        let canMark = tiered && this.editable() && Boolean(team);
        let done = tiersDone(team, tierOpen);
        let best = team ? bestTier(team, tierOpen) : 0;
        // what can be done on this tile: its tiers, or for an ordinary tile its one drop, in the picked team's colour
        let drops = tiered
            ? tile.tiers.map((tier, k) => ({ tier: TIERS[k].id, name: TIERS[k].name, color: TIERS[k].color, task: tier.task, points: tier.points, done: done.includes(TIERS[k].id) }))
            : [{ tier: 0, name: 'Drop', color: team ? team.color : '#ffcf3f', task: tile.title || `Tile ${tierOpen + 1}`, points: tile.points, done: Boolean(team && team.completed.includes(tierOpen)) }];
        // a player sends in for their own team when they are on one, else for the team picked
        let forTeam = this.myTeam() || team;
        let forDone = (drop) => Boolean(forTeam && (drop.tier > 0 ? tiersDone(forTeam, tierOpen).includes(drop.tier) : forTeam.completed.includes(tierOpen)));
        let waiting = (drop) => forTeam && mySubmissions.some((sub) => sub.teamId === forTeam.id && sub.tile === tierOpen && sub.tier === drop.tier);
        let close = () => this.setState({ tierOpen: null, tierError: null });
        let hint = canMark ? `Tick the tiers ${team.name} has completed. Only the best one counts for points.`
            : tiered ? (team ? `${team.name}'s progress on this tile. Only the best tier a team completes counts.` : 'Only the best tier a team completes counts for points.')
            : team ? `${team.name}'s progress on this tile.` : null;
        return (
            <div className="bingo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { close(); } }}
                onKeyDown={(e) => { if (e.key === 'Escape') { close(); } }}>
                <div className="bingo-tile-form bingo-tier-dialog" role="dialog" aria-modal="true" aria-labelledby="bingo-tier-title"
                    style={team ? { '--team': team.color } : undefined}>
                    <div className="bingo-tier-head">
                        {tile.image ? <img src={tile.image} alt="" /> : null}
                        <h2 id="bingo-tier-title">{tile.title || `Tile ${tierOpen + 1}`}</h2>
                    </div>
                    {hint ? <p className="bingo-hint">{hint}</p> : null}
                    {/* a player looking at another team's board still sends drops in for their own */}
                    {!canMark && forTeam && team && forTeam.id !== team.id ? <p className="bingo-hint">Drops you submit go in for your team, {forTeam.name}.</p> : null}
                    <ul className="bingo-tier-list">
                        {drops.map((drop) => {
                            let inner = (
                                <>
                                    <span className="bingo-medal is-done" aria-hidden="true" />
                                    <span className="bingo-tier-text">
                                        <span className="bingo-tier-label">{drop.name}</span>
                                        <span className="bingo-tier-task">{drop.task}</span>
                                    </span>
                                    <span className="bingo-tier-points">{drop.points} {drop.points === 1 ? 'pt' : 'pts'}</span>
                                </>
                            );
                            return (
                                <li key={drop.tier} className={(drop.done ? 'is-done' : '') + (tiered && best === drop.tier ? ' is-best' : '')} style={{ '--medal': drop.color }}>
                                    {canMark ?
                                        <label className="bingo-tier-row">
                                            <input type="checkbox" checked={drop.done} disabled={saving} onChange={() => this.toggleTier(tierOpen, drop.tier)} />
                                            {inner}
                                        </label>
                                        :
                                        <div className="bingo-tier-row">
                                            <span className="bingo-tier-tick" aria-label={drop.done ? 'Completed' : 'Not completed'}>{drop.done ? '✓' : ''}</span>
                                            {inner}
                                            {/* a player sends in a drop their team hasn't got yet */}
                                            {!forDone(drop) ?
                                                waiting(drop) ?
                                                    <span className="bingo-tier-waiting">Waiting for review</span>
                                                    :
                                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small"
                                                        onClick={() => this.openSubmit({ tile: tierOpen, tier: drop.tier })}>
                                                        {account ? 'Submit' : 'Submit…'}
                                                    </button>
                                                : null}
                                        </div>}
                                </li>
                            );
                        })}
                    </ul>
                    {team && tiered ?
                        <p className="bingo-tier-score">
                            {best > 0 ? <>Counts for {team.name}: <strong>{TIERS[best - 1].name}, {tileScore(team, tiles, tierOpen)} {tileScore(team, tiles, tierOpen) === 1 ? 'point' : 'points'}</strong></>
                                : `${team.name} hasn't completed a tier yet.`}
                        </p>
                        : null}
                    {tierError ? <p className="bingo-error" role="alert">{tierError}</p> : null}
                    <div className="bingo-tile-form-actions">
                        <button type="button" className="bingo-btn bingo-btn-primary" autoFocus onClick={close}>Done</button>
                    </div>
                </div>
            </div>
        );
    }

    // Every drop a player can send in, for the form's list: [{ value: "tile:tier", label }]
    dropChoices() {
        let choices = [];
        this.state.tiles.forEach((tile, i) => {
            if (!tile.title && !tile.image) {
                return;
            }
            let name = tile.title || `Tile ${i + 1}`;
            if ((tile.tiers || []).length > 0) {
                tile.tiers.forEach((tier, k) => choices.push({ value: `${i}:${k + 1}`, label: `${name} · ${TIERS[k].name}: ${tier.task} (${tier.points} pts)` }));
            } else {
                choices.push({ value: `${i}:0`, label: `${name} (${tile.points} pts)` });
            }
        });
        return choices;
    }

    // Open the form for sending a drop in, with a drop already chosen ({ tile, tier }) or not
    openSubmit(drop) {
        this.setState({
            submitOpen: true,
            submitTeam: this.myTeam() ? String(this.myTeam().id) : this.state.selected !== null ? String(this.state.selected) : '',
            submitDrop: drop ? `${drop.tile}:${drop.tier}` : '',
            submitImages: [''], submitNote: '', submitError: null, submitDone: false
        });
    }

    sendDrop = (e) => {
        e.preventDefault();
        let { submitTeam, submitDrop, submitImages, submitNote } = this.state;
        let images = submitImages.map((link) => link.trim()).filter((link) => link);
        if (!submitTeam) {
            this.setState({ submitError: 'Choose your team' });
            return;
        }
        if (!submitDrop) {
            this.setState({ submitError: 'Choose the drop' });
            return;
        }
        if (images.length === 0 || !images.every(isImageLink)) {
            this.setState({ submitError: images.length === 0 ? 'Add a link to a picture of the drop' : 'Each picture link has to start with https://' });
            return;
        }
        let [tile, tier] = submitDrop.split(':').map(Number);
        this.setState({ saving: true, submitError: null });
        api.bingo.submitDrop({ teamId: Number(submitTeam), tile, tier, images, note: submitNote.trim() }).then(() => {
            if (this.mounted) {
                this.setState({ saving: false, submitDone: true });
                this.loadSubmissions();
            }
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            if (this.mounted) {
                this.setState({ saving: false, submitError: message || "Couldn't send that in. Please try again." });
            }
        });
    }

    // The form for sending a drop in: the team, the drop, a link to a picture of it and a note. Players log in first.
    renderSubmitForm() {
        let { account, teams, submitTeam, submitDrop, submitImages, submitNote, submitError, submitDone, saving } = this.state;
        let mine = this.myTeam();
        let close = () => this.setState({ submitOpen: false });
        let setImage = (k, value) => this.setState({ submitImages: submitImages.map((link, j) => (j === k ? value : link)), submitError: null });
        let body;
        if (!account) {
            body = (
                <>
                    <p className="bingo-hint">Log in with your clomp account to send in a drop. You'll come straight back here.</p>
                    <div className="bingo-tile-form-actions">
                        <button type="button" className="bingo-btn bingo-btn-ghost" onClick={close}>Cancel</button>
                        <Link className="bingo-btn bingo-btn-primary" to="/login?next=/bingo">Log in</Link>
                    </div>
                </>
            );
        } else if (submitDone) {
            body = (
                <>
                    <p className="bingo-done" role="status">Sent! An admin will check it and mark it on the board.</p>
                    <div className="bingo-tile-form-actions">
                        <button type="button" className="bingo-btn bingo-btn-ghost" onClick={() => this.openSubmit(null)}>Send another</button>
                        <button type="button" className="bingo-btn bingo-btn-primary" autoFocus onClick={close}>Done</button>
                    </div>
                </>
            );
        } else {
            body = (
                <>
                    <label htmlFor="bingo-submit-team">Team</label>
                    {mine ?
                        <p className="bingo-submit-team" id="bingo-submit-team" style={{ '--team': mine.color }}>
                            <span className="bingo-team-swatch" aria-hidden="true" />{mine.name}<span className="bingo-optional"> · you're on this team</span>
                        </p>
                        :
                        <select id="bingo-submit-team" value={submitTeam} onChange={(e) => this.setState({ submitTeam: e.target.value, submitError: null })}>
                            <option value="">Choose your team</option>
                            {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                        </select>}
                    <label htmlFor="bingo-submit-drop">Drop</label>
                    <select id="bingo-submit-drop" value={submitDrop} onChange={(e) => this.setState({ submitDrop: e.target.value, submitError: null })}>
                        <option value="">Choose the drop</option>
                        {this.dropChoices().map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                    </select>
                    <label htmlFor="bingo-submit-image-0">Pictures of the drop <span className="bingo-optional">(more than one if the tile has parts)</span></label>
                    {submitImages.map((link, k) => (
                        <div key={k} className="bingo-image-link">
                            {isImageLink(link.trim()) ? <img src={link.trim()} alt={`Drop shot ${k + 1}`} /> : null}
                            <input id={`bingo-submit-image-${k}`} type="url" maxLength={500} aria-label={k > 0 ? `Picture ${k + 1}` : undefined}
                                placeholder={k === 0 ? 'https://… (a screenshot link, e.g. from Imgur or Discord)' : 'https://… another picture'}
                                value={link} onChange={(e) => setImage(k, e.target.value)} />
                            {submitImages.length > 1 ?
                                <button type="button" className="bingo-image-remove" aria-label={`Remove picture ${k + 1}`} title="Remove this picture"
                                    onClick={() => this.setState({ submitImages: submitImages.filter((_, j) => j !== k), submitError: null })}>✕</button>
                                : null}
                        </div>
                    ))}
                    {submitImages.length < SUBMIT_IMAGES_MAX ?
                        <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small bingo-image-add"
                            onClick={() => this.setState({ submitImages: [...submitImages, ''] })}>+ Add another picture</button>
                        : null}
                    <label htmlFor="bingo-submit-note">Note for the admins <span className="bingo-optional">(optional)</span></label>
                    <textarea id="bingo-submit-note" rows="2" maxLength={NOTE_MAX} value={submitNote}
                        placeholder="Anything the admins should know: a problem with the screenshot, a concern, a question"
                        onChange={(e) => this.setState({ submitNote: e.target.value })} />
                    {submitError ? <p className="bingo-error" role="alert">{submitError}</p> : null}
                    <div className="bingo-tile-form-actions">
                        <span className="bingo-count">Logged in as {account.username}</span>
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={close}>Cancel</button>
                        <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving}>{saving ? 'Sending...' : 'Submit'}</button>
                    </div>
                </>
            );
        }
        return (
            <div className="bingo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { close(); } }}
                onKeyDown={(e) => { if (e.key === 'Escape') { close(); } }}>
                <form className="bingo-tile-form bingo-submit-form" role="dialog" aria-modal="true" aria-labelledby="bingo-submit-title" onSubmit={this.sendDrop}>
                    <h2 id="bingo-submit-title">Submit a drop</h2>
                    {body}
                </form>
            </div>
        );
    }

    // The colour a team is drawn in (the board gives every team one, chosen or by default)
    teamColor(teamId) {
        let team = this.state.teams.find((other) => other.id === teamId);
        return team ? team.color : '#ffcf3f';
    }

    // A submitted drop, in words: "Raid · Gold" or the tile's name
    dropName(sub) {
        let tile = this.state.tiles[sub.tile];
        let name = tile ? tile.title || `Tile ${sub.tile + 1}` : `Tile ${sub.tile + 1}`;
        return sub.tier > 0 ? `${name} · ${TIERS[sub.tier - 1].name}` : name;
    }

    // A player's own drops waiting to be checked, under the scoreboard
    renderMySubmissions() {
        let { mySubmissions } = this.state;
        if (mySubmissions.length === 0) {
            return null;
        }
        return (
            <section className="bingo-subs" aria-label="Your drops waiting to be checked">
                <h2>Your drops <span>waiting to be checked</span></h2>
                <ul>
                    {mySubmissions.map((sub) => (
                        <li key={sub.id} style={{ '--team': this.teamColor(sub.teamId) }}>
                            <span className="bingo-sub-what"><span className="bingo-team-swatch" aria-hidden="true" />{this.dropName(sub)}</span>
                            <span className="bingo-sub-meta">{sub.team} · {timeAgo(sub.createdAt)}</span>
                        </li>
                    ))}
                </ul>
            </section>
        );
    }

    // Approve a drop (it is marked done for its team) or deny it (it goes)
    review(sub, approve) {
        this.setState({ reviewing: sub.id, reviewError: null });
        let request = ++this.requests;
        (approve ? api.bingo.approveSubmission(sub.id) : api.bingo.denySubmission(sub.id)).then((data) => {
            if (approve) {
                this.show(request, data);
            }
            if (this.mounted) {
                this.setState({ reviewing: null, confirmingDeny: null, submissions: data.submissions });
            }
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            if (this.mounted) {
                this.setState({ reviewing: null, confirmingDeny: null, reviewError: message || "Couldn't save that. Please try again." });
            }
            this.loadSubmissions();
        });
    }

    // For admins, under the scoreboard: the drops players have sent in, each with its picture, to approve or deny
    renderSubmissions() {
        let { submissions, reviewing, confirmingDeny, reviewError } = this.state;
        return (
            <section className="bingo-subs" aria-label="Submissions">
                <h2>Submissions {submissions.length > 0 ? <span className="bingo-subs-count">{submissions.length}</span> : null}</h2>
                {reviewError ? <p className="bingo-error" role="alert">{reviewError}</p> : null}
                {submissions.length === 0 ?
                    <p className="bingo-hint">No drops waiting to be checked.</p>
                    :
                    <ul>
                        {submissions.map((sub) => {
                            let busy = reviewing === sub.id;
                            return (
                                <li key={sub.id} style={{ '--team': this.teamColor(sub.teamId) }}>
                                    <a className="bingo-sub-image" href={(sub.images || [sub.image])[0]} target="_blank" rel="noopener noreferrer" title="Open the picture">
                                        <img src={(sub.images || [sub.image])[0]} alt={`Sent in for ${this.dropName(sub)}`} />
                                        {(sub.images || []).length > 1 ? <span className="bingo-sub-image-count">1/{sub.images.length}</span> : null}
                                    </a>
                                    <div className="bingo-sub-body">
                                        <span className="bingo-sub-what"><span className="bingo-team-swatch" aria-hidden="true" />{sub.team}: {this.dropName(sub)}</span>
                                        <span className="bingo-sub-meta">by {sub.by.username} · {timeAgo(sub.createdAt)}</span>
                                        {(sub.images || []).length > 1 ?
                                            <span className="bingo-sub-more">
                                                {sub.images.slice(1).map((link, k) => (
                                                    <a key={k} href={link} target="_blank" rel="noopener noreferrer" title={`Open picture ${k + 2}`}>
                                                        <img src={link} alt={`Shot ${k + 2} of ${this.dropName(sub)}`} />
                                                    </a>
                                                ))}
                                            </span>
                                            : null}
                                        {sub.note ? <span className="bingo-sub-note">“{sub.note}”</span> : null}
                                        <span className="bingo-sub-actions">
                                            {confirmingDeny === sub.id ?
                                                <>
                                                    <button type="button" className="bingo-btn bingo-btn-danger bingo-btn-small" disabled={busy} onClick={() => this.review(sub, false)}>Yes, deny</button>
                                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={busy} onClick={() => this.setState({ confirmingDeny: null })}>Keep</button>
                                                </>
                                                :
                                                <>
                                                    <button type="button" className="bingo-btn bingo-btn-primary bingo-btn-small" disabled={busy} onClick={() => this.review(sub, true)}>{busy ? 'Saving...' : 'Approve'}</button>
                                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={busy} onClick={() => this.setState({ confirmingDeny: sub.id })}>Deny</button>
                                                </>}
                                        </span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>}
            </section>
        );
    }

    renderEvent() {
        if (this.state.loading) {
            return <p className="bingo-status">Loading the board...</p>;
        }
        if (this.state.error) {
            return <p className="bingo-status">Couldn't load the board right now. Please try again later.</p>;
        }
        let scored = this.scored();
        let { signups, draft, teams, account, accountChecked, startsAt, saving, draftError } = this.state;
        let view = this.view();
        // the tabs: sign-ups while they are open or once anyone has signed up, the draft once it has started (for
        // admins, always); none at all when there is only the board
        let tabs = [['board', 'Board']];
        if (this.editable() || (signups && (signups.open || signups.signups.length > 0))) {
            tabs.push(['signups', `Sign-ups${signups && signups.signups.length > 0 ? ` (${signups.signups.length})` : ''}`]);
        }
        if (this.editable() || draft.status !== 'off') {
            tabs.push(['draft', draft.status === 'live' ? 'Draft · Live' : 'Draft']);
        }
        if (!tabs.some(([id]) => id === view)) {
            view = 'board';
        }
        return (
            <>
                {tabs.length > 1 ?
                    <nav className="bingo-tabs" aria-label="Event">
                        {tabs.map(([id, name]) => (
                            <button key={id} type="button" className={'bingo-tab' + (id === view ? ' is-active' : '') + (id === 'draft' && draft.status === 'live' ? ' is-live' : '')}
                                aria-current={id === view ? 'page' : undefined} onClick={() => this.setState({ view: id })}>{name}</button>
                        ))}
                    </nav>
                    : null}
                {view === 'signups' ?
                    <BingoSignups account={account} accountChecked={accountChecked} data={signups} admin={this.editable()} teams={teams}
                        startsAt={startsAt} onChanged={this.loadSignups} />
                    : view === 'draft' ?
                        <BingoDraft draft={draft} teams={teams} signups={signups} account={account} admin={this.editable()} saving={saving}
                            error={draftError} change={(send) => this.change(send, 'draftError')} />
                        :
                        /* side by side on a wide screen: the scoreboard on the left, the board beside it */
                        <div className="bingo-layout">
                            <div className="bingo-side">
                                {this.renderScores(scored)}
                                {this.editable() ? this.renderSubmissions() : this.renderMySubmissions()}
                            </div>
                            <div className="bingo-main">{this.renderBoard(scored)}</div>
                        </div>}
                {this.editable() && this.state.tileOpen !== null ? this.renderTileForm() : null}
                {this.state.tierOpen !== null ? this.renderTierDialog(scored) : null}
                {this.state.submitOpen ? this.renderSubmitForm() : null}
            </>
        );
    }

    // The clomp login, at the top of the page: who is logged in and whether they can edit, or a way to log in
    renderAccount() {
        let { account, accountChecked, canEdit, playerView } = this.state;
        if (!accountChecked) {
            return <div className="bingo-account" />;
        }
        if (canEdit && playerView) {
            return (
                <div className="bingo-account bingo-player-view" role="status">
                    <span className="bingo-account-who"><strong>Player view.</strong> You see the page as a player does, {account ? <>logged in as <strong>{account.username}</strong>, </> : null}and can send in your own drops. The admin controls are hidden.</span>
                    <button type="button" className="bingo-btn bingo-btn-primary bingo-btn-small" onClick={() => this.setPlayerView(false)}>Back to admin view</button>
                </div>
            );
        }
        return (
            <div className="bingo-account">
                {account ?
                    <>
                        <span className="bingo-account-who">Logged in as <strong>{account.username}</strong></span>
                        <span className={'bingo-account-role' + (canEdit ? ' is-admin' : '')}>{canEdit ? 'Admin' : 'Player'}</span>
                        {canEdit ?
                            <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" title="See the page as a player, to take part in the event yourself: the admin controls are hidden"
                                onClick={() => this.setPlayerView(true)}>Player view</button>
                            : null}
                        <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" onClick={this.logOut}>Log out</button>
                    </>
                    :
                    <>
                        <span className="bingo-account-who">Log in with your clomp account to sign up and send in drops.</span>
                        <Link className="bingo-btn bingo-btn-primary bingo-btn-small" to="/login?next=/bingo">Log in</Link>
                    </>}
            </div>
        );
    }

    render() {
        return (
            <div className="bingo-page">
                {/* an admin in player view gets a bar of its own, above everything */}
                {this.state.canEdit && this.state.playerView ? this.renderAccount() : null}
                <header className="bingo-top">
                    {this.renderTitle()}
                    {this.state.loading || this.state.error ? null : this.renderTimer()}
                    {this.state.canEdit && this.state.playerView ? null : this.renderAccount()}
                </header>
                {this.renderEvent()}
            </div>
        );
    }

}
